import { Chip } from '@mui/material';
import type { ReactElement } from 'react';

import useStyles from './Badge.styles';

/**
 * Badge component to display a styled label with status and size.
 *
 * @param label - Text to display inside the badge.
 * @param status - Visual style of the badge, based on status. Default is 'DEFAULT'.
 * @param size - Size of the badge. Default is 'LARGE'.
 * @param icon - Optional element shown before the label (e.g. SVG via `?react` import).
 * @param dataTestId - Optional test ID for querying in tests.
 *
 * @see Badge design: https://www.figma.com/design/tHuavQeTzVIG2XBlMfBhrh/Commercial---Design-System-1.5?m=auto&node-id=250-7708
 * @todo Add support for icons, close icon, outline, and additional statuses.
 */

export enum BadgeStatus {
  DEFAULT = 'DEFAULT',
  SUCCESS = 'SUCCESS',
  DANGER = 'DANGER',
  INFO = 'INFO',
  WARNING = 'WARNING',
  TEAL = 'TEAL',
  PURPLE_GRADIENT = 'PURPLE_GRADIENT',
  WHITE = 'WHITE',
}

export enum BadgeSize {
  SMALL = 'small',
  LARGE = 'large',
}

interface BadgeProps {
  label: React.ReactNode;
  status?: BadgeStatus;
  size?: BadgeSize;
  border?: string;
  /** Leading icon; must be a single React element (MUI Chip constraint). */
  icon?: ReactElement;
  dataTestId?: string;
  /** Override the root element (e.g. 'span' when rendered inside a button). */
  component?: React.ElementType;
}

const Badge = ({
  label,
  status = BadgeStatus.DEFAULT,
  size = BadgeSize.LARGE,
  border = '1px solid',
  icon,
  dataTestId,
  component,
}: BadgeProps) => {
  const { classes } = useStyles({ status, size, border });

  return (
    <Chip
      data-testid={dataTestId}
      label={label}
      className={classes.chip}
      icon={icon}
      {...(component && { component })}
    />
  );
};

export default Badge;
